import { Head, Link, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Category } from '@/types'
import { useState } from 'react'
import { PlusIcon, PencilIcon, Trash2Icon, FolderOpenIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  categories: Category[]
}

const kindColors = {
  revenue: 'bg-success/8 text-success border-success/15',
  cost: 'bg-destructive/8 text-destructive border-destructive/15',
  transfer: 'bg-primary/8 text-primary border-primary/15',
}

export default function Index({ categories }: Props) {
  const [deleteId, setDeleteId] = useState<number | null>(null)

  return (
    <>
      <Head title="Categories" />
      <PageHeader title="Categories" description="Organize your transactions with categories">
        <Link href="/categories/new">
          <Button size="sm" className="gap-1.5">
            <PlusIcon className="h-3.5 w-3.5" />
            New Category
          </Button>
        </Link>
      </PageHeader>

      <PageContent>
        {categories.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-3.5">
                <FolderOpenIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-3 text-[15px] font-medium text-foreground">No categories yet</h3>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Create your first category to organize transactions.
              </p>
              <Link href="/categories/new" className="mt-4">
                <Button size="sm">Create Category</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Name</TableHead>
                    <TableHead className="text-[13px] font-medium text-muted-foreground">Type</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id} className="group">
                      <TableCell>
                        <Link
                          href={`/categories/${category.id}`}
                          className="text-[13px] font-medium text-foreground hover:text-primary transition-colors"
                        >
                          {category.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn('capitalize text-[11px]', kindColors[category.kind])}
                        >
                          {category.kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <Link href={`/categories/${category.id}/edit`}>
                            <Button variant="ghost" size="icon-sm">
                              <PencilIcon className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/8"
                            onClick={() => setDeleteId(category.id)}
                          >
                            <Trash2Icon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </PageContent>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={() => setDeleteId(null)}
        title="Delete category?"
        description="This action cannot be undone. Transactions using this category will be left uncategorized."
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleteId) router.delete(`/categories/${deleteId}`)
        }}
      />
    </>
  )
}
